import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { ClubApi } from "../../services/clubApi";
import { devError, devLog } from "../../utils/devLog";

const getAgencyUserDisplayName = (user: any) => {
  const fullName = String(user?.fullName || "").trim();
  if (fullName) return fullName;

  return `${String(user?.firstName || "").trim()} ${String(user?.lastName || "").trim()}`.trim();
};

const ClubTrialClaimPage = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [status, setStatus] = useState("Preparing your trial case key...");
  const [sent, setSent] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");
  const freeCasePayload = {
    email: user?.primaryEmailAddress?.emailAddress || "",
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    displayName: getAgencyUserDisplayName(user),
  };
  const hasRequiredTrialIdentity = Boolean(
    String(freeCasePayload.firstName || "").trim() || String(freeCasePayload.displayName || "").trim()
  );

  const sendTrialEmail = async () => {
    const trialRequestStorageKey = user?.id ? `club_trial_key_requested_${user.id}` : "";
    const response = await ClubApi.requestFreeDigitalCase({
      ...freeCasePayload,
    });

    if (response?.sent) {
      if (trialRequestStorageKey) {
        window.localStorage.setItem(trialRequestStorageKey, "true");
      }
      setSent(true);
      setError("");
      devLog("api", "agency_trial_claim_succeeded", {
        route: "/agency/trial",
        userId: String(user?.id || ""),
        email: freeCasePayload.email,
      });
      navigate("/agency/dashboard", { replace: true });
      return;
    }

    if (response?.skipped) {
      setError(`Trial email was skipped: ${response?.reason || "configuration missing"}.`);
      return;
    }

    setError("Unable to send trial case email right now. Please try again.");
  };

  useEffect(() => {
    let isMounted = true;
    const claimTrial = async () => {
      if (!user) return;
      if (!hasRequiredTrialIdentity) {
        setError("Your Clerk profile needs a first name or display name before claiming trial access.");
        return;
      }
      try {
        setStatus("Creating your member profile...");
        await ClubApi.bootstrapMe({
          ...freeCasePayload,
        });

        setStatus("Sending your digital trial case key...");
        await sendTrialEmail();
      } catch (err: any) {
        if (!isMounted) return;
        devError("api", "agency_trial_claim_failed", {
          route: "/agency/trial",
          userId: String(user?.id || ""),
          status: err?.response?.status,
          message: String(err?.message || ""),
          response: err?.response?.data || null,
        });
        setError(err?.response?.data?.message || "Unable to send your trial case right now.");
      }
    };

    claimTrial();
    return () => {
      isMounted = false;
    };
  }, [hasRequiredTrialIdentity, navigate, user, user?.firstName, user?.lastName, user?.primaryEmailAddress?.emailAddress]);

  return (
    <section className="mx-auto max-w-2xl px-4 pb-10 pt-6 text-white">
      <div className="rounded-3xl border border-sky-300/30 bg-gradient-to-br from-[#061325] via-[#081b2e] to-[#0f293b] p-7 shadow-[0_30px_70px_rgba(0,0,0,0.5)]">
        <p className="text-xs uppercase tracking-[0.28em] text-sky-200">Trial Access</p>
        <div className="mt-4 overflow-hidden rounded-full border border-sky-200/30 bg-black/25 p-1">
          <div className="h-2 animate-pulse rounded-full bg-gradient-to-r from-sky-300 via-sky-100 to-sky-300" />
        </div>
        <div className="mt-3 flex items-center gap-2">
          <span className="h-2.5 w-2.5 animate-bounce rounded-full bg-sky-300 [animation-delay:-0.25s]" />
          <span className="h-2.5 w-2.5 animate-bounce rounded-full bg-sky-200 [animation-delay:-0.1s]" />
          <span className="h-2.5 w-2.5 animate-bounce rounded-full bg-sky-100" />
          <span className="ml-2 text-xs uppercase tracking-[0.18em] text-sky-100/80">Sending trial key</span>
        </div>
        <h1 className="mt-3 text-3xl font-black leading-tight md:text-4xl">
          Check your email for your exclusive digital trial case link
        </h1>
        <p className="mt-4 text-sm text-sky-100/90">
          {sent
            ? "Your trial case key has been sent. Open your inbox to access the case."
            : status}
        </p>
        {sent && (
          <p className="mt-4 rounded-lg border border-emerald-400/40 bg-emerald-500/10 p-3 text-sm text-emerald-200">
            Email sent successfully. If you do not see it, check spam/promotions.
          </p>
        )}
        {error && (
          <p className="mt-4 rounded-lg border border-red-400/40 bg-red-500/10 p-3 text-sm text-red-200">
            {error}
          </p>
        )}
        <div className="mt-6 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={async () => {
              try {
                setResending(true);
                setStatus("Resending your trial case email...");
                await sendTrialEmail();
              } catch (err: any) {
                setError(err?.response?.data?.message || "Unable to resend trial email right now.");
              } finally {
                setResending(false);
              }
            }}
            disabled={resending}
            className="rounded-lg border border-sky-200/45 px-4 py-2 text-sm font-semibold text-sky-100 hover:bg-sky-400/10 disabled:opacity-60"
          >
            {resending ? "Sending..." : "Send Email Again"}
          </button>
          <Link
            to="/agency/dashboard"
            className="rounded-lg bg-sky-300 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-sky-200"
          >
            Back to My Agency
          </Link>
          <Link
            to="/agency"
            className="rounded-lg border border-white/25 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10"
          >
            View Membership Plans
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ClubTrialClaimPage;
